import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getRequiredCurrentUserClientId } from "../../../core/auth/tenant";
import {
  createUser,
  deleteUser,
  getUsers,
  updateUser,
} from "../services/usersService";
import {
  extractUserList,
  toAPIUser,
  toAPIUserStatusUpdate,
  toAPIUserUpdate,
  toUIUser,
} from "../services/userMappers";

const getErrorMessage = (error, fallback) => {
  if (typeof error === "string") return error;
  if (error?.message) return error.message;
  return fallback;
};

export const fetchUsersThunk = createAsyncThunk(
  "users/fetchUsers",
  async (_, { getState, rejectWithValue }) => {
    let clientId;
    try {
      clientId = getRequiredCurrentUserClientId(getState());
    } catch (error) {
      return rejectWithValue(error.message);
    }

    const data = await getUsers(clientId, { limit: 100, offset: 0 });
    if (data?.error) {
      return rejectWithValue(data.error.message || "Unable to load users.");
    }

    const list = extractUserList(data);

    return {
      users: list.map(toUIUser),
      total: data?.total ?? list.length,
    };
  },
);

export const addUserThunk = createAsyncThunk(
  "users/addUser",
  async (formData, { dispatch, getState, rejectWithValue }) => {
    let clientId;
    try {
      clientId = getRequiredCurrentUserClientId(getState());
    } catch (error) {
      return rejectWithValue(error.message);
    }

    // The first user added to an organization is always made the owner,
    // regardless of the role the service/admin account selected. Once an owner
    // exists, every subsequent user keeps the chosen role.
    const existingResponse = await getUsers(clientId, { limit: 100, offset: 0 });
    const existingUsers = extractUserList(existingResponse);
    const hasOwner = existingUsers.some(
      (user) => String(user.user_role || "").trim().toLowerCase() === "owner",
    );
    const resolvedRole = hasOwner ? formData.userRole || "viewer" : "owner";

    const body = {
      ...toAPIUser(formData),
      client_id: clientId,
      user_type: "console",
      user_role: resolvedRole,
    };

    const data = await createUser(body);
    if (data?.error) {
      return rejectWithValue(data.error.message || "Unable to add user.");
    }
    const createdUserId = data?.id ?? data?.user?.id ?? null;

    await dispatch(fetchUsersThunk());
    return { success: true, userId: createdUserId };
  },
);

export const updateUserThunk = createAsyncThunk(
  "users/updateUser",
  async ({ id, formData, existingUser }, { dispatch, rejectWithValue }) => {
    const body = toAPIUserUpdate(formData, existingUser);
    const data = await updateUser(id, body);
    if (data?.error) {
      return rejectWithValue(data.error.message || "Unable to update user.");
    }

    await dispatch(fetchUsersThunk());
    return { success: true };
  },
);

export const deleteUserThunk = createAsyncThunk(
  "users/deleteUser",
  async (id, { rejectWithValue }) => {
    const data = await deleteUser(id);
    if (data?.error) {
      return rejectWithValue(data.error.message || "Unable to delete user.");
    }

    return { id };
  },
);

export const bulkDeleteUsersThunk = createAsyncThunk(
  "users/bulkDeleteUsers",
  async (ids, { dispatch, rejectWithValue }) => {
    const results = await Promise.all(ids.map((id) => deleteUser(id)));
    const failed = results.filter((result) => result?.error);

    await dispatch(fetchUsersThunk());

    if (failed.length > 0) {
      return rejectWithValue(`Failed to delete ${failed.length} user(s).`);
    }

    return { success: true };
  },
);

export const bulkStatusChangeThunk = createAsyncThunk(
  "users/bulkStatusChange",
  async ({ ids, status }, { dispatch, getState, rejectWithValue }) => {
    const users = getState().users.users;

    const results = await Promise.all(
      ids.map((id) => {
        const user = users.find((item) => item.id === id);
        if (!user) return Promise.resolve({});

        return updateUser(id, toAPIUserStatusUpdate(user, status));
      }),
    );

    const failed = results.filter((result) => result?.error);

    await dispatch(fetchUsersThunk());

    if (failed.length > 0) {
      return rejectWithValue(`Failed to update ${failed.length} user(s).`);
    }

    return { success: true };
  },
);

const initialState = {
  users: [],
  isLoading: false,
  isProcessing: false,
  error: null,
  total: 0,
};

const usersSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    clearUsersError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsersThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUsersThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.users = action.payload.users;
        state.total = action.payload.total;
      })
      .addCase(fetchUsersThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = getErrorMessage(action.payload, "Unable to load users.");
      })
      .addCase(addUserThunk.pending, (state) => {
        state.isProcessing = true;
        state.error = null;
      })
      .addCase(addUserThunk.fulfilled, (state) => {
        state.isProcessing = false;
      })
      .addCase(addUserThunk.rejected, (state, action) => {
        state.isProcessing = false;
        state.error = getErrorMessage(action.payload, "Unable to add user.");
      })
      .addCase(updateUserThunk.pending, (state) => {
        state.isProcessing = true;
        state.error = null;
      })
      .addCase(updateUserThunk.fulfilled, (state) => {
        state.isProcessing = false;
      })
      .addCase(updateUserThunk.rejected, (state, action) => {
        state.isProcessing = false;
        state.error = getErrorMessage(action.payload, "Unable to update user.");
      })
      .addCase(deleteUserThunk.pending, (state) => {
        state.isProcessing = true;
        state.error = null;
      })
      .addCase(deleteUserThunk.fulfilled, (state, action) => {
        state.isProcessing = false;
        state.users = state.users.filter((user) => user.id !== action.payload.id);
        state.total = Math.max(state.total - 1, 0);
      })
      .addCase(deleteUserThunk.rejected, (state, action) => {
        state.isProcessing = false;
        state.error = getErrorMessage(action.payload, "Unable to delete user.");
      })
      .addCase(bulkDeleteUsersThunk.pending, (state) => {
        state.isProcessing = true;
        state.error = null;
      })
      .addCase(bulkDeleteUsersThunk.fulfilled, (state) => {
        state.isProcessing = false;
      })
      .addCase(bulkDeleteUsersThunk.rejected, (state, action) => {
        state.isProcessing = false;
        state.error = getErrorMessage(action.payload, "Unable to delete selected users.");
      })
      .addCase(bulkStatusChangeThunk.pending, (state) => {
        state.isProcessing = true;
        state.error = null;
      })
      .addCase(bulkStatusChangeThunk.fulfilled, (state) => {
        state.isProcessing = false;
      })
      .addCase(bulkStatusChangeThunk.rejected, (state, action) => {
        state.isProcessing = false;
        state.error = getErrorMessage(action.payload, "Unable to update selected users.");
      });
  },
});

export const { clearUsersError } = usersSlice.actions;

export const selectUsers = (state) => state.users.users;
export const selectUsersLoading = (state) => state.users.isLoading;
export const selectUsersProcessing = (state) => state.users.isProcessing;
export const selectUsersError = (state) => state.users.error;
export const selectUsersTotal = (state) => state.users.total;

export default usersSlice.reducer;
